// --- S3_FEATURE_START ---
import { useFile } from "@/hooks/use-files";
// --- S3_FEATURE_END ---
import { cn } from "@/lib/utils";

interface UserAvatarProps {
  /** Pass the user domain object, not destructured primitives (see frontend rules). */
  user: { name?: string | null; image?: string | null };
  className?: string;
}

function initials(name?: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  const joined = parts.map((p) => p.charAt(0).toUpperCase()).join("");
  return joined || "?";
}

/**
 * Renders a user's avatar. `user.image` may hold a direct URL (e.g. from an
 * OAuth provider) or a bare uploaded file id, resolved to a short-lived
 * presigned URL. Anything unresolved falls back to the user's initials.
 * --- S3_FEATURE_START ---
 * (`ProfileAvatarCard` is what stores the file id.)
 * --- S3_FEATURE_END ---
 *
 * This component ships in every fork: the sidebar and dashboard header both
 * render it, so with the S3 feature disabled it degrades to direct URLs +
 * initials rather than disappearing from the chrome.
 */
export function UserAvatar({ user, className }: UserAvatarProps) {
  const image = user.image ?? null;
  const isDirectUrl =
    image !== null &&
    (image.startsWith("http") || image.startsWith("/") || image.startsWith("data:"));

  // A non-URL `user.image` is an uploaded file id, which only the S3 feature can
  // resolve. With S3 stripped this collapses to `null`, so such an image falls
  // through to initials — keep the `?? null` tail on its own line so the block
  // strips to a valid `const fileUrl: string | null = null;`.
  const fileUrl: string | null =
    // --- S3_FEATURE_START ---
    useFile(isDirectUrl ? null : image).data?.url ??
    // --- S3_FEATURE_END ---
    null;
  const src = isDirectUrl ? image : fileUrl;

  return (
    <span
      className={cn(
        "inline-flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-xs font-medium text-primary",
        className,
      )}
    >
      {src ? (
        <img src={src} alt={user.name ?? "Avatar"} className="h-full w-full object-cover" />
      ) : (
        initials(user.name)
      )}
    </span>
  );
}
